import type {ValidationError} from 'fastest-validator';

import Validator from 'fastest-validator';
import {action, makeObservable, observable} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {Field, ValidationRule} from './types';

import {isTestEnv} from '../test-utils/isTestEnv';
import {valueIsDefined} from './valueIsDefined';

export const createField = <T = any>(
  name: string,
  {
    customValidate,
    defaultValue,
    extraFields = {},
    validationRule,
  }: {
    customValidate?: (value: T) => boolean;
    defaultValue?: T;
    extraFields?: {[key: string]: any};
    validationRule?: ValidationRule;
  },
): Field<T> => {
  const formField: Field = {
    clear: () => {
      formField.value = defaultValue;
      formField.error = null;
      formField.isValid = null;
    },
    error: null,
    extraFields,
    isValid: null,
    setValue: (value: T) => {
      formField.value = value;

      formField.validate();
    },
    validate: () => {
      let isValid = true;

      if (customValidate) {
        isValid = customValidate(formField.value);
      } else if (validationRule) {
        const valueIsDefined_ = valueIsDefined(formField.value);

        if (!validationRule.optional && !valueIsDefined_) {
          isValid = false;

          if (validationRule.messages && validationRule.messages.required) {
            formField.error = validationRule.messages.required;
          } else {
            formField.error = i18n.t('shared:validation.field_required');
          }
        } else if (valueIsDefined_) {
          const v = new Validator();

          const check = v.compile({
            [name]: validationRule,
          });

          const result = check({[name]: formField.value});

          if (typeof result === 'boolean') {
            isValid = result;

            formField.error = null;
          } else {
            const errors = result as ValidationError[];

            isValid = !errors.length;

            formField.error = errors[0]?.message || null;
          }
        } else {
          isValid = true;

          formField.error = null;
        }
      }

      formField.isValid = isValid;

      return isValid;
    },
    validationRule,
    value: defaultValue,
  };

  return makeObservable(formField, {
    clear: !isTestEnv ? action.bound : false,
    error: observable,
    extraFields: observable,
    isValid: observable,
    setValue: !isTestEnv ? action.bound : false,
    validate: !isTestEnv ? action.bound : false,
    value: observable,
  });
};
